import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import { Dialog, DialogContent, DialogTitle } from '@mui/material';

import { getOrganizations } from '@src/api/organizations';
import { updateUsers } from '@src/api/users';
import { menuItems, UiRoutes } from '@src/lib/constants';
import { userAction } from '@src/redux/action/login';
import { ReduxProps } from '@src/redux/type';

import {
  Brand,
  BrandLogo,
  BrandName,
  ChevronIcon,
  DropdownDivider,
  DropdownItem,
  DropdownMenu,
  HeaderWrap,
  MobileMenuButton,
  MobileNavLink,
  MobileNavOverlay,
  MobileNavPanel,
  NavLink,
  NavLinks,
  OrgItem,
  OrgSwitchGrid,
  UserAvatar,
  UserInfo,
  UserName,
  UserRole,
  UserSection,
} from './style';

function Headerbar() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();

  const { userData } = useSelector((state: ReduxProps) => state?.loginReducer.loginState);
  const role = userData?.role ?? '';
  const isSuperAdmin = role === 'super_admin';

  const path = userData?.org?.org_id ? `/${userData?.org?.org_id}` : '';
  const navItems = menuItems(role, path);

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [dropdownPos, setDropdownPos] = useState({ top: 69, right: 24 });
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [orgSwitchOpen, setOrgSwitchOpen] = useState(false);
  const [orgList, setOrgList] = useState<any[]>([]);

  const dropdownRef = useRef<HTMLDivElement>(null);

  const openDropdown = useCallback(() => {
    if (dropdownRef.current) {
      const rect = dropdownRef.current.getBoundingClientRect();
      setDropdownPos({
        top: rect.bottom + 4,
        right: window.innerWidth - rect.right,
      });
    }

    setDropdownOpen(v => !v);
  }, []);

  const initials =
    [userData?.firstName, userData?.lastName]
      .filter(Boolean)
      .map(s => s![0])
      .join('')
      .toUpperCase() || '?';

  const displayName = [userData?.firstName, userData?.lastName].filter(Boolean).join(' ') || userData?.email || '';

  const formattedRole = role.replace(/_/g, ' ');

  const isActive = (route: string): boolean => {
    const normalize = (p: string) => p.replace(/\/+$/, '');
    const current = normalize(location.pathname);
    const base = normalize(route);
    return current === base || current.startsWith(`${base}/`);
  };

  const handleLogout = () => {
    localStorage.clear();
    window.location.reload();
  };

  const handleSwitchOrg = async () => {
    setDropdownOpen(false);

    if (!orgList.length) {
      try {
        const res = await getOrganizations();
        setOrgList(res.results);
      } catch {
        // ignore
      }
    }

    setOrgSwitchOpen(true);
  };

  const updateUserOrg = async (orgDetails: any) => {
    try {
      dispatch(userAction({ ...userData, org: orgDetails }));
      await updateUsers({
        [userData?.id || userData?._id || 'f']: { org_url: orgDetails.id },
      });
    } catch {
      // ignore
    } finally {
      setOrgSwitchOpen(false);
    }
  };

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <>
      <HeaderWrap>
        <Brand onClick={() => navigate(path + UiRoutes.HOME)}>
          <BrandLogo src='/images/logo.png' alt='Logo' />
        </Brand>

        <NavLinks>
          {navItems.map(({ route, label }) => (
            <NavLink key={route} to={route} $active={isActive(route)}>
              {label}
            </NavLink>
          ))}
        </NavLinks>

        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
          <MobileMenuButton onClick={() => setMobileNavOpen(v => !v)} aria-label='Toggle menu'>
            <svg width='20' height='20' viewBox='0 0 20 20' fill='currentColor'>
              <rect x='2' y='4' width='16' height='2' rx='1' />
              <rect x='2' y='9' width='16' height='2' rx='1' />
              <rect x='2' y='14' width='16' height='2' rx='1' />
            </svg>
          </MobileMenuButton>

          <div ref={dropdownRef}>
            <UserSection onClick={openDropdown}>
              <UserAvatar>{initials}</UserAvatar>
              <UserInfo>
                <UserName>{displayName}</UserName>
                <UserRole>{formattedRole}</UserRole>
              </UserInfo>
              <ChevronIcon $open={dropdownOpen} viewBox='0 0 16 16' fill='none' stroke='currentColor' strokeWidth='2'>
                <polyline points='4 6 8 10 12 6' />
              </ChevronIcon>
            </UserSection>

            {dropdownOpen && (
              <DropdownMenu $top={dropdownPos.top} $right={dropdownPos.right}>
                {isSuperAdmin && (
                  <>
                    <DropdownItem onClick={handleSwitchOrg}>
                      <svg
                        width='16'
                        height='16'
                        viewBox='0 0 16 16'
                        fill='none'
                        stroke='currentColor'
                        strokeWidth='1.5'
                      >
                        <path d='M2 8a6 6 0 1 0 12 0A6 6 0 0 0 2 8z' />
                        <path d='M8 4v4l3 2' />
                      </svg>
                      Switch Organisation
                    </DropdownItem>
                    <DropdownDivider />
                  </>
                )}
                <DropdownItem onClick={handleLogout}>
                  <svg width='16' height='16' viewBox='0 0 16 16' fill='none' stroke='currentColor' strokeWidth='1.5'>
                    <path d='M6 2H3a1 1 0 0 0-1 1v10a1 1 0 0 0 1 1h3' />
                    <polyline points='10 11 14 8 10 5' />
                    <line x1='14' y1='8' x2='6' y2='8' />
                  </svg>
                  Logout
                </DropdownItem>
              </DropdownMenu>
            )}
          </div>
        </div>
      </HeaderWrap>

      {/* Mobile nav drawer */}
      <MobileNavOverlay $open={mobileNavOpen} onClick={() => setMobileNavOpen(false)}>
        <MobileNavPanel onClick={e => e.stopPropagation()}>
          {navItems.map(({ route, label }) => (
            <MobileNavLink key={route} to={route} $active={isActive(route)} onClick={() => setMobileNavOpen(false)}>
              {label}
            </MobileNavLink>
          ))}
        </MobileNavPanel>
      </MobileNavOverlay>

      {/* Switch org dialog */}
      <Dialog open={orgSwitchOpen} onClose={() => setOrgSwitchOpen(false)}>
        <DialogTitle>Switch Organisation</DialogTitle>
        <DialogContent>
          <OrgSwitchGrid>
            {orgList.map((item: any) => (
              <OrgItem key={item.name} onClick={() => updateUserOrg(item)}>
                {item.name}
              </OrgItem>
            ))}
          </OrgSwitchGrid>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default Headerbar;
